import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, Calendar, MessageSquare, Settings, LogOut,
  Edit, Clock, MessageCircle, Wallet, BookOpen
} from "lucide-react";
import { Sparkles } from "lucide-react";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useBooking } from "../../context/BookingContext";
import { useReviews } from "../../context/ReviewsContext";
import { supabase } from "../../lib/supabase";
import ExpertOverviewTab from "../../components/dashboard/ExpertOverviewTab";
import ExpertConsultationsTab from "../../components/dashboard/ExpertConsultationsTab";
import ExpertClientsTab from "../../components/dashboard/ExpertClientsTab";
import ExpertProfileTab from "../../components/dashboard/ExpertProfileTab";
import ExpertReviewsTab from "../../components/dashboard/ExpertReviewsTab";
import ExpertSettingsTab from "../../components/dashboard/ExpertSettingsTab";
import PayoutSettings from "../../components/ui/PayoutSettings";
import ExpertArticlesTab from "../../components/ui/ExpertArticlesTab";

export default function ExpertDashboard() {
  const { user, logout, updateUser } = useAuth();
  const { bookings, getBookingsForExpert, completeBooking } = useBooking();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedClientForGoals, setSelectedClientForGoals] = useState(null);
  const { getReviewsForExpert, saveExpertReply } = useReviews();
  const [expertReviews, setExpertReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);

  const [profileLoading, setProfileLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  const [profile, setProfile] = useState({
    name: user?.name || "Эксперт",
    title: user?.title || "",
    bio: user?.bio || "",
    hourlyRate: user?.hourly_rate || 0,
    specializations: user?.specializations || [],
    companies: user?.companies || [],
    achievements: user?.achievements || [],
    available: user?.available !== undefined ? user.available : true
  });

  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");

  const handleAvatarChange = (newUrl) => {
    setAvatarUrl(newUrl);
  };

  useEffect(() => {
    if (!user?.id) return;
    let mounted = true;
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("name, title, bio, hourly_rate, specializations, companies, achievements, available, avatar_url")
          .eq("id", user.id)
          .single();
        if (!error && data && mounted) {
          setProfile({
            name: data.name || user.name || "Эксперт",
            title: data.title || "",
            bio: data.bio || "",
            hourlyRate: data.hourly_rate || 0,
            specializations: data.specializations || [],
            companies: data.companies || [],
            achievements: data.achievements || [],
            available: data.available !== undefined ? data.available : true
          });
          if (data.avatar_url) setAvatarUrl(data.avatar_url);
        }
      } catch (err) {
        console.warn("Failed to load expert profile:", err);
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };
    loadProfile();
    return () => { mounted = false; };
  }, [user?.id]);

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    setProfileSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          name: profile.name,
          title: profile.title,
          bio: profile.bio,
          hourly_rate: profile.hourlyRate,
          specializations: profile.specializations,
          companies: profile.companies,
          achievements: profile.achievements,
          available: profile.available,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
      if (error) {
        console.error("Failed to save profile:", error);
      } else {
        setProfileSaved(true);
        if (updateUser) {
          updateUser({
            name: profile.name,
            title: profile.title,
            bio: profile.bio,
            hourly_rate: profile.hourlyRate,
            specializations: profile.specializations,
            companies: profile.companies,
            achievements: profile.achievements,
            available: profile.available
          });
        }
        setTimeout(() => setProfileSaved(false), 3000);
      }
    } catch (err) {
      console.error("Save profile error:", err);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const expertId = user?.id || "expert-1";
  const expertBookings = getBookingsForExpert(expertId);
  const completedBookings = expertBookings.filter((b) => b.status === "completed");
  const upcomingBookings = expertBookings.filter((b) => b.status !== "completed" && b.status !== "cancelled");

  const uniqueClients = [...new Map(
    expertBookings.map((b) => [b.clientId, { id: b.clientId, name: b.clientName || "Клиент" }])
  ).values()];

  const stats = {
    views: 1247,
    consultations: expertBookings.length || 32,
    rating: 4.8,
    reviews: 28,
    earnings: 384000
  };

  useEffect(() => {
    if (!reviewsLoaded && expertId) {
      const loadReviews = async () => {
        const reviews = await getReviewsForExpert(expertId);
        setExpertReviews(reviews);
        setReviewsLoaded(true);
      };
      loadReviews();
    }
  }, [expertId, reviewsLoaded, getReviewsForExpert]);

  const averageRating = expertReviews.length > 0
    ? (expertReviews.reduce((sum, r) => sum + r.rating, 0) / expertReviews.length).toFixed(1)
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: expertReviews.filter((r) => r.rating === star).length,
    percentage: expertReviews.length > 0
      ? (expertReviews.filter((r) => r.rating === star).length / expertReviews.length) * 100
      : 0
  }));

  const handleReplyToReview = async (review, replyText) => {
    const success = await saveExpertReply(review.id, replyText);
    if (success) {
      setExpertReviews((prev) =>
        prev.map((r) =>
          r.id === review.id
            ? { ...r, expertReply: replyText, replyDate: new Date().toISOString() }
            : r
        )
      );
    }
  };

  const tabs = [
    { id: "overview", label: "Обзор", icon: User },
    { id: "profile", label: "Профиль", icon: Edit },
    { id: "consultations", label: "Консультации", icon: Calendar },
    { id: "clients", label: "Мои клиенты", icon: MessageSquare },
    { id: "reviews", label: "Отзывы", icon: MessageCircle },
    { id: "articles", label: "Мои статьи", icon: BookOpen },
    { id: "schedule", label: "Расписание", icon: Clock },
    { id: "payout", label: "Выплаты", icon: Wallet },
    { id: "settings", label: "Настройки", icon: Settings }
  ];

  return (
    <Layout hideFooter>
      <div id="expert-dashboard_page" className="min-h-screen bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-64 flex-shrink-0">
              <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800 mb-6">
                <div className="flex items-center gap-4 mb-4">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={profile.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                      <span className="text-white text-xl font-medium">
                        {profile.name[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-medium text-white">
                      {profile.name}
                    </h2>
                    <p className="text-lg font-extralight text-neutral-400">Эксперт</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      profile.available ? "bg-green-400" : "bg-neutral-500"
                    }`}
                  />
                  <span className="text-lg font-extralight text-neutral-400">
                    {profile.available ? "Доступен" : "Недоступен"}
                  </span>
                </div>
              </div>

              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (tab.id === "schedule") {
                        navigate("/expert/schedule");
                      } else {
                        setActiveTab(tab.id);
                        setSelectedBooking(null);
                        setSelectedClientForGoals(null);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                      activeTab === tab.id
                        ? "bg-primary-500/10 text-primary-400"
                        : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="text-lg font-extralight">{tab.label}</span>
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-lg font-extralight">Выйти</span>
                </button>
              </nav>
            </aside>

            <main className="flex-1">
              {activeTab === "overview" && (
                <ExpertOverviewTab
                  user={user}
                  stats={stats}
                  averageRating={averageRating}
                  expertReviews={expertReviews}
                  upcomingBookings={upcomingBookings}
                  completedBookings={completedBookings}
                  completeBooking={completeBooking}
                  setActiveTab={setActiveTab}
                  setSelectedBooking={setSelectedBooking}
                  setSelectedClientForGoals={setSelectedClientForGoals}
                  navigate={navigate}
                  profile={profile}
                  setProfile={setProfile}
                  handleSaveProfile={handleSaveProfile}
                  onboardingComplete={onboardingComplete}
                  setOnboardingComplete={setOnboardingComplete}
                  avatarUrl={avatarUrl}
                  handleAvatarChange={handleAvatarChange}
                />
              )}

              {activeTab === "consultations" && (
                <ExpertConsultationsTab
                  selectedBooking={selectedBooking}
                  setSelectedBooking={setSelectedBooking}
                  upcomingBookings={upcomingBookings}
                  completedBookings={completedBookings}
                  expertBookings={expertBookings}
                  completeBooking={completeBooking}
                  expertProfile={profile}
                />
              )}

              {activeTab === "clients" && (
                <ExpertClientsTab
                  selectedClientForGoals={selectedClientForGoals}
                  setSelectedClientForGoals={setSelectedClientForGoals}
                  uniqueClients={uniqueClients}
                  expertBookings={expertBookings}
                  expertId={expertId}
                  profileName={profile.name}
                />
              )}

              {activeTab === "profile" && (
                <ExpertProfileTab
                  user={user}
                  profile={profile}
                  setProfile={setProfile}
                  avatarUrl={avatarUrl}
                  handleAvatarChange={handleAvatarChange}
                  handleSaveProfile={handleSaveProfile}
                  profileSaving={profileSaving}
                  profileSaved={profileSaved}
                />
              )}

              {activeTab === "reviews" && (
                <ExpertReviewsTab
                  expertReviews={expertReviews}
                  averageRating={averageRating}
                  ratingDistribution={ratingDistribution}
                  handleReplyToReview={handleReplyToReview}
                />
              )}

              {activeTab === "articles" && (
                <ExpertArticlesTab expertId={expertId} />
              )}

              {activeTab === "payout" && (
                <PayoutSettings />
              )}

              {activeTab === "settings" && (
                <ExpertSettingsTab user={user} updateUser={updateUser} />
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}